import {
  AmenitiesAdaAdaIcon,
  AmenitiesPetsPetFriendlyIcon,
  Button,
  Divider,
  EButtonColorVariant,
  EHeadingStyleVariant,
  EIconButtonVariant,
  ETextStyleVariant,
  GeneralCaretLeftIcon,
  Heading,
  IconButton,
  LinkButton,
  Modal,
  NumberInput,
  Text,
  Toggle,
} from '@outdoorsyco/bonfire';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useInitialGuests } from '@/hooks/useInitialInputs';
import { IHeaderSearchFilterGuests } from '@/redux/modules/globalHeader';

type TMobileSearchGuestsModalContentProps = {
  onCloseHandler: () => void;
  guests?: Partial<IHeaderSearchFilterGuests>;
  onChangeGuests?: (guests: IHeaderSearchFilterGuests) => void;
  isGuestOccupancyAvailable?: boolean;
  isCampground?: boolean;
};

export const MobileSearchGuestsModalContent = ({
  onCloseHandler,
  guests,
  onChangeGuests,
  isGuestOccupancyAvailable,
  isCampground,
}: TMobileSearchGuestsModalContentProps) => {
  const intl = useIntl();

  const [adultsValue, setAdultsValue] = useState(guests?.adults || 1);
  const [childrenValue, setChildrenValue] = useState(guests?.children || 0);
  const [infantsValue, setInfantsValue] = useState(guests?.infants || 0);
  const [petFriendlyValue, setPetFriendlyValue] = useState(guests?.pet_friendly || false);
  const [accessibleValue, setAccessibleValue] = useState(guests?.accessible || false);

  const { hasChanges, isInResetState, resetToInitial } = useInitialGuests(
    {
      adults: adultsValue,
      children: childrenValue,
      infants: infantsValue,
      pet_friendly: petFriendlyValue,
      accessible: accessibleValue,
    },
    true,
  );

  const onResetHandler = () => {
    setAdultsValue(0);
    setChildrenValue(0);
    setInfantsValue(0);
    setPetFriendlyValue(false);
    setAccessibleValue(false);

    onChangeGuests?.({
      adults: 0,
      children: 0,
      infants: 0,
      pet_friendly: false,
      accessible: false,
    });
  };

  const onConfirmHandler = () => {
    const newGuests = {
      adults: adultsValue || undefined,
      children: childrenValue || undefined,
      ...(isGuestOccupancyAvailable && { infants: infantsValue || undefined }),
      pet_friendly: petFriendlyValue || undefined,
      accessible: accessibleValue || undefined,
      seatbelts: guests?.seatbelts || undefined,
    };
    onChangeGuests?.(newGuests);
    onCloseHandler();
  };

  const getContent = () => {
    return (
      <div className="flex flex-col p-0 pb-6 pointer-events-auto md:p-4 gap-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <Text variant={ETextStyleVariant.MediumBold}>
              <FormattedMessage defaultMessage="Adults" id="rePX4c" />
            </Text>
            <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-50">
              <FormattedMessage defaultMessage="Ages 13 or above" id="7GtkzA" />
            </Text>
          </div>
          <NumberInput
            ariaLabel={intl.formatMessage({
              defaultMessage: 'Adults number input',
              id: 'QI9jVh',
            })}
            value={adultsValue}
            onChange={setAdultsValue}
            minValue={1}
            maxValue={10}
          />
        </div>
        <Divider />
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <Text variant={ETextStyleVariant.MediumBold}>
              <FormattedMessage defaultMessage="Children" id="kzlY5R" />
            </Text>
            <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-50">
              {isGuestOccupancyAvailable ? (
                <FormattedMessage defaultMessage="Ages 3-12" id="cn4PHX" />
              ) : (
                <FormattedMessage defaultMessage="Ages 12 or under" id="SAdssW" />
              )}
            </Text>
          </div>
          <NumberInput
            ariaLabel={intl.formatMessage({
              defaultMessage: 'Children number input',
              id: 'ElS1Or',
            })}
            value={childrenValue}
            onChange={setChildrenValue}
            maxValue={10}
          />
        </div>
        <Divider />
        {isGuestOccupancyAvailable && (
          <>
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <Text variant={ETextStyleVariant.MediumBold}>
                  <FormattedMessage defaultMessage="Infants" id="wHlQeN" />
                </Text>
                <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-50">
                  <FormattedMessage defaultMessage="Ages 2 or under" id="/cJQwH" />
                </Text>
              </div>
              <NumberInput
                ariaLabel={intl.formatMessage({
                  defaultMessage: 'Infants number input',
                  id: 'EBh6e/',
                })}
                value={infantsValue}
                onChange={setInfantsValue}
                maxValue={10}
              />
            </div>
            <Divider />
          </>
        )}
        <div className="flex items-center justify-between my-2">
          <div className="flex items-center gap-2">
            <AmenitiesPetsPetFriendlyIcon className="text-xl font-medium" />
            <Text variant={ETextStyleVariant.MediumBold}>
              <FormattedMessage defaultMessage="Pet friendly" id="c8hn0D" />
            </Text>
          </div>
          <Toggle
            isSelected={petFriendlyValue}
            ariaLabel={intl.formatMessage({
              defaultMessage: 'Pet friendly toggle',
              id: 'gOLubl',
            })}
            onChange={setPetFriendlyValue}
          />
        </div>
        {!isCampground && (
          <>
            <Divider />
            <div className="flex items-center justify-between my-2">
              <div className="flex items-center gap-2">
                <AmenitiesAdaAdaIcon className="text-xl font-medium" />
                <Text variant={ETextStyleVariant.MediumBold}>
                  <FormattedMessage defaultMessage="ADA accessible" id="MlZmsn" />
                </Text>
              </div>
              <Toggle
                isSelected={accessibleValue}
                ariaLabel={intl.formatMessage({
                  defaultMessage: 'ADA accessible toggle',
                  id: 'R6Zaoq',
                })}
                onChange={setAccessibleValue}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <Modal.Header inlineHeader={true} className="flex items-center gap-2 !mt-6">
        <IconButton
          icon={GeneralCaretLeftIcon}
          onClick={() => {
            const initialValue = resetToInitial();
            setAdultsValue(initialValue?.adults || 1);
            setChildrenValue(initialValue?.children || 0);
            setInfantsValue(initialValue?.infants || 0);
            setPetFriendlyValue(initialValue?.pet_friendly || false);
            setAccessibleValue(initialValue?.accessible || false);
            onCloseHandler();
          }}
          className="text-2xl"
          variant={EIconButtonVariant.Ghost}
        />
        <Heading variant={EHeadingStyleVariant.H6}>
          <FormattedMessage defaultMessage="Add guests" id="pP5ifN" />
        </Heading>
      </Modal.Header>
      <Modal.Content className="h-full !mb-0 !my-4 !px-4">{getContent()}</Modal.Content>
      <Modal.Footer className="p-0">
        <div className="w-full">
          <div className="flex items-center justify-between p-4">
            <LinkButton
              label={intl.formatMessage({ defaultMessage: 'Clear', id: '/GCoTA' })}
              onClick={() => onResetHandler()}
              disabled={isInResetState}
            />
            <Button
              variant={EButtonColorVariant.Primary}
              label={intl.formatMessage({ defaultMessage: 'Done', id: 'JXdbo8' })}
              onClick={onConfirmHandler}
              disabled={!hasChanges}
            />
          </div>
        </div>
      </Modal.Footer>
    </>
  );
};
